import { Link } from "react-router-dom";
import { BarChart3, Download, Grid2x2, LayoutPanelLeft, Lock, MonitorSmartphone, ShieldCheck, Siren, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi } from "@/lib/api";
import { fmtBytes, fmtDate } from "@/lib/format";
import { LegalNotice } from "@/components/app/LegalNotice";

type LatestApk = { version_name: string; version_code: number; size: number; sha256: string; notes: string; published_at: string | null; download_url: string } | null;

const FEATURES = [
  { icon: MonitorSmartphone, t: "Jumelage en 30 secondes", d: "Lancez l'application sur une TV Android, saisissez le code à 6 chiffres dans le portail : c'est prêt." },
  { icon: Grid2x2, t: "Murs vidéo synchronisés", d: "Assemblez 2 à 4 télés côte à côte ou empilées : une seule vidéo, étendue sur tous les écrans." },
  { icon: LayoutPanelLeft, t: "Écran partagé en zones", d: "Vidéo, météo, flux RSS et horloge en même temps, chacun dans sa zone." },
  { icon: Siren, t: "Alerte d'urgence", d: "Un message plein écran sur toutes vos télés, en un clic, puis retour automatique au contenu." },
  { icon: BarChart3, t: "Preuve de diffusion", d: "Ce qui a joué, où et combien de fois, avec export CSV." },
  { icon: ShieldCheck, t: "Multi-organisation sécurisé", d: "Chaque organisation ne voit que ses écrans, ses médias et ses utilisateurs." },
];

function DownloadCard() {
  const { data, loading } = useApi<LatestApk>("/public/apk/latest");
  return (
    <div id="telecharger" data-testid="landing-download" className="panel relative overflow-hidden p-8 md:p-10">
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-orange-500/10 blur-3xl" />
      <div className="relative grid gap-8 md:grid-cols-[1.2fr_1fr] md:items-center">
        <div>
          <p className="eyebrow text-orange-400">Application TV Android</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-white">Téléchargez le lecteur Innova-Sign</h2>
          <ol className="mt-5 space-y-2 text-sm text-slate-300">
            <li>1. Sur la TV Android, autorisez l'installation d'applications de sources inconnues.</li>
            <li>2. Téléchargez et installez le fichier APK ci-contre (clé USB ou navigateur de la TV).</li>
            <li>3. Ouvrez l'application : un code à 6 chiffres s'affiche, saisissez-le dans le portail.</li>
          </ol>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-6">
          {loading ? <p className="text-sm text-slate-500">Chargement…</p> : data ? (
            <>
              <div className="flex items-center gap-3"><Smartphone className="h-8 w-8 text-orange-400" /><div><p data-testid="landing-apk-version" className="font-display text-2xl font-semibold text-white">Version {data.version_name}</p><p className="text-xs text-slate-500">{fmtBytes(data.size)}{data.published_at ? ` · publiée ${fmtDate(data.published_at)}` : ""}</p></div></div>
              {data.notes && <p className="mt-3 whitespace-pre-line text-xs text-slate-400">{data.notes}</p>}
              <a href={data.download_url} data-testid="landing-apk-download" className="mt-5 inline-flex h-12 w-full items-center justify-center rounded-md bg-orange-600 font-semibold text-white shadow-lg shadow-orange-500/20 transition-colors hover:bg-orange-500"><Download className="mr-2 h-5 w-5" />Télécharger l'APK</a>
              <p className="mt-3 break-all font-mono text-[10px] text-slate-600">SHA-256 {data.sha256}</p>
            </>
          ) : <p data-testid="landing-apk-unavailable" className="text-sm text-slate-400">L'application sera bientôt disponible au téléchargement.</p>}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <img src="/img/logo-innova-sign.png" alt="Innova-Sign" className="h-12 w-auto" />
        <div className="flex gap-2">
          <a href="#telecharger" data-testid="landing-nav-download" className="hidden h-10 items-center rounded-md px-4 text-sm text-slate-300 hover:text-white sm:inline-flex">Application TV</a>
          <Link to="/login" data-testid="landing-login"><Button className="bg-blue-600 text-white hover:bg-blue-500"><Lock className="mr-1 h-4 w-4" />Se connecter</Button></Link>
        </div>
      </header>
      <section className="relative mx-auto max-w-6xl overflow-hidden px-6 pb-16 pt-8">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div className="rise">
            <p className="eyebrow text-cyan-400">Affichage numérique · Cloud</p>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl">Vos télés deviennent des écrans qui vendent.</h1>
            <p className="mt-5 max-w-lg text-base text-slate-300">Menus, promotions, vidéos, météo et alertes : pilotez toutes vos TV Android depuis un seul portail.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#telecharger" data-testid="landing-hero-download"><Button className="h-12 bg-orange-600 px-6 text-white hover:bg-orange-500"><Download className="mr-2 h-5 w-5" />Télécharger l'app TV</Button></a>
              <Link to="/login"><Button variant="outline" className="h-12 border-slate-700 bg-transparent px-6 text-slate-200 hover:bg-slate-800">Accéder au portail</Button></Link>
            </div>
          </div>
          <div className="relative">
            <img src="/img/login-signage.jpg" alt="Écran d'affichage numérique" className="aspect-[4/3] w-full rounded-2xl border border-slate-800 object-cover shadow-2xl shadow-black/60" />
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div key={f.t} className="rise panel p-6" style={{ animationDelay: `${i * 60}ms` }}>
              <f.icon className="h-6 w-6 text-orange-400" />
              <p className="mt-4 font-medium text-slate-100">{f.t}</p>
              <p className="mt-1 text-sm text-slate-400">{f.d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-6 pb-20"><DownloadCard /></section>
      <footer className="mx-auto max-w-6xl border-t border-slate-800/80 px-6 py-6"><LegalNotice /></footer>
    </div>
  );
}
